import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePickerField } from "@/components/shared/DatePickerField";
import type { FiscalAgendaObligation } from "@/services/fiscalService";

export interface MarkFiscalCompleteDialogProps {
  obligation: FiscalAgendaObligation | null;
  onClose: () => void;
  onConfirm: (
    completedDate: string,
    notes: string,
    amountPaidUyu?: number | null,
  ) => void;
  isSubmitting: boolean;
  /**
   * When provided, the dialog renders a pre-filled "amount paid" input. Used
   * for IVA obligations where we know what was due. The submitted value flows
   * back through `onConfirm`'s third argument and the caller should forward it
   * to `markFiscalObligationCompleted({ amountPaidUyu })`. Pass null/undefined
   * to hide the input.
   */
  defaultAmountUyu?: number | null;
}

export function MarkFiscalCompleteDialog({
  obligation,
  onClose,
  onConfirm,
  isSubmitting,
  defaultAmountUyu,
}: MarkFiscalCompleteDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const [completedDateStr, setCompletedDateStr] = useState("");
  const [notesStr, setNotesStr] = useState("");
  const [amountStr, setAmountStr] = useState("");
  const showAmountInput =
    defaultAmountUyu !== undefined && defaultAmountUyu !== null;

  useEffect(() => {
    if (obligation) {
      setCompletedDateStr(
        obligation.dueDate.includes("T")
          ? obligation.dueDate.slice(0, 10)
          : obligation.dueDate
      );
      setNotesStr("");
      setAmountStr(
        defaultAmountUyu !== undefined && defaultAmountUyu !== null
          ? String(defaultAmountUyu)
          : "",
      );
    }
  }, [obligation, defaultAmountUyu]);

  if (!obligation) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!completedDateStr.trim()) return;
    let amountPaidUyu: number | null | undefined;
    if (showAmountInput) {
      const trimmed = amountStr.trim();
      if (trimmed === "") {
        amountPaidUyu = null;
      } else {
        const parsed = Number(trimmed);
        amountPaidUyu = Number.isFinite(parsed) ? parsed : null;
      }
    }
    onConfirm(completedDateStr.trim(), notesStr.trim(), amountPaidUyu);
  };

  return (
    <Dialog open={!!obligation} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("fiscalAgenda.markCompleteDialogTitle")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DatePickerField
            value={completedDateStr}
            onChange={setCompletedDateStr}
            label={t("fiscalAgenda.markCompleteDialogDateLabel")}
          />
          {showAmountInput ? (
            <div className="space-y-2">
              <Label htmlFor="mark-complete-amount">
                {t("fiscalAgenda.markCompleteDialogAmountLabel", "Monto pagado (UYU)")}
              </Label>
              <Input
                id="mark-complete-amount"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "fiscalAgenda.markCompleteDialogAmountHint",
                  "Se registra en el historial. Podés ajustarlo después.",
                )}
              </p>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="mark-complete-notes">
              {t("fiscalAgenda.markCompleteDialogNotesLabel")}
            </Label>
            <textarea
              id="mark-complete-notes"
              value={notesStr}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setNotesStr(e.target.value)
              }
              placeholder={t("fiscalAgenda.markCompleteDialogNotesPlaceholder")}
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none transition-colors duration-150"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("fiscalAgenda.dialogCancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !completedDateStr}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("fiscalAgenda.markCompleteDialogSubmit")
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
